import { Component, inject, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import {
  DashboardLayoutService,
  type DashboardApiWidget,
  type WidgetCatalogEntry,
} from '../../../core/services/dashboard-layout.service';
import { isMonthlyReport1CardId } from '../../../core/enums/dashboard-cards.enum';
import { IconComponentV2 } from '../icon-component/icon.component';
import { DashboardCatalogWidgetFormDialogComponent } from './dashboard-catalog-widget-form-dialog.component';
import { WidgetPlatform } from '../../../core/enums/dashboard.enum';
import { catchError, finalize } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';

export type DashboardWidgetPickerAction =
  | 'saveAs'
  | 'setDefault'
  | 'delete'
  | 'toggleMixed'
  | 'widgets'
  | 'reset'
  | 'selectLayout';

export interface DashboardLayoutCardItem {
  key: string;
  title: string;
  selected: boolean;
  sourcePageName?: string | null;
  widgetId?: number;
  sourcePageId?: number | null;
  colSpan?: number | null;
  rowSpan?: number | null;
  customConfig?: Record<string, any> | null;
}

export interface DashboardWidgetPickerDialogData {
  mode?: 'widgets' | 'layout';
  pageId?: number; // Added pageId
  entries: WidgetCatalogEntry[];
  /** Initially selected widget catalog ids */
  selectedIds: number[];
  /** Restrict to monthly-report-1 known keys only */
  monthlyReport1Only?: boolean;
  /** Visibility cards used for layout mode */
  cards?: DashboardLayoutCardItem[];
  currentLayoutId?: number | null;
  layouts?: Array<{ id: number; name: string; is_default?: boolean }>;
  isMixedMode?: boolean;
  layoutError?: boolean;
  /** When true, hides the backend-dependent action buttons (Save As, Set Default, Delete, etc.) */
  hideActions?: boolean;
  /** Optional per-action hide map for layout action buttons. */
  hiddenLayoutActions?: Partial<Record<DashboardWidgetPickerAction, boolean>>;
}

export type DashboardWidgetPickerDialogResult =
  | { kind: 'widgets'; selectedIds: number[] }
  | {
      kind: 'layoutVisibility';
      selectedKeys: string[];
      /** When true (Set default), parent restores page snapshot layout and shows all selected widgets. */
      resetLayoutToDefaults?: boolean;
      mixedContext?: {
        isMixed: boolean;
        dashboardId: number | null;
        visibleWidgets: Array<{
          widget_id: string;
          source_page_id?: string | null;
          col_span?: number | null;
          row_span?: number | null;
          custom_config?: Record<string, any> | null;
        }>;
      };
    }
  | {
      kind: 'layoutAction';
      action: DashboardWidgetPickerAction;
      dashboardId?: number;
    };

@Component({
  standalone: true,
  selector: 'app-dashboard-widget-picker-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    IconComponentV2,
  ],
  styles: [`
    ::ng-deep .custom-scrollbar::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    ::ng-deep .custom-scrollbar::-webkit-scrollbar-track {
      background: transparent;
    }
    ::ng-deep .custom-scrollbar::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 10px;
    }
    ::ng-deep .dark .custom-scrollbar::-webkit-scrollbar-thumb {
      background: #475569;
    }
    ::ng-deep .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: #94a3b8;
    }
    ::ng-deep .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: #64748b;
    }
  `],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <h2 mat-dialog-title class="!w-full text-lg font-semibold !text-slate-900 bg-white dark:bg-slate-900 dark:!text-white flex items-center gap-3 !px-5 !pt-5 !pb-3 border-b border-slate-100 dark:border-slate-800 !m-0">
      <span class="shrink-0">{{ mode() === 'layout' ? 'Customize Layout' : 'Add Widgets' }}</span>
      @if (mode() === 'layout' && !data.hideActions && !isLayoutActionHidden('toggleMixed')) {
        <button
          type="button"
          class="!ml-auto inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium transition-all hover:shadow-sm"
          [ngClass]="
            isMixed()
              ? '!border-[#AD46FF]/30 bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-300'
              : '!border-slate-200 dark:!border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
          "
          (click)="onToggleMixed()"
        >
          <span class="relative inline-flex h-4 w-8 items-center rounded-full transition-colors"
            [ngClass]="
              isMixed()
                ? 'bg-[#AD46FF]'
                : 'bg-slate-200 dark:bg-slate-700'
            "
          >
            <span
              class="inline-block h-3 w-3 transform rounded-full bg-white shadow-sm transition-transform"
              [ngClass]="isMixed() ? 'translate-x-4' : 'translate-x-0.5'"
            ></span>
          </span>
          <span class="inline-flex items-center gap-1">
            <app-icon-v2 name="blend" [size]="14" [customClass]="isMixed() ? 'text-[#AD46FF]' : 'text-slate-400'"></app-icon-v2>
            {{ isMixed() ? 'Mixed On' : 'Mixed Off' }}
          </span>
        </button>
      }
    </h2>
    <mat-dialog-content class="max-h-[70vh] overflow-y-auto min-w-[340px] sm:min-w-[620px] bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 !p-5 custom-scrollbar">
      @if (mode() === 'layout') {
        <div class="space-y-4">
          @if (data.layoutError) {
            <div class="flex items-center gap-2 p-2.5 rounded-xl bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200/50 dark:border-amber-500/20">
              <app-icon-v2 name="alert-triangle" [size]="16"></app-icon-v2>
              <p class="text-sm font-medium mb-0">Layout API unavailable - using defaults</p>
            </div>
          }

          @if (!data.hideActions && layouts().length > 1) {
            <div class="flex flex-wrap items-center justify-end gap-2.5 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
              <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Switch layout</label>
              <select
                class="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1 text-sm font-medium text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-[#AD46FF]/20 focus:border-[#AD46FF] outline-none transition-all"
                [value]="selectedLayoutId() ?? ''"
                (change)="onLayoutChange($event)"
              >
                @for (d of layouts(); track d.id) {
                  <option [value]="d.id">
                    {{ d.name || ('Layout ' + d.id) }}{{ d.is_default ? ' (default)' : '' }}
                  </option>
                }
              </select>
              <button type="button" mat-stroked-button class="!rounded-lg !border-slate-200 dark:!border-slate-700 !h-8 !px-3" [disabled]="!layoutChanged()" (click)="emitLayoutSwitch()">
                Switch
              </button>
            </div>
          }

          <div>
            <div class="flex items-center justify-between mb-3">
              <div class="text-sm font-semibold text-slate-900 dark:text-white">Show or hide cards</div>
              <div class="text-xs font-medium text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800">
                {{ selectedLayoutKeys().size }} / {{ displayCards().length }} visible
              </div>
            </div>
            
            @if (mixedLoading()) {
              <div class="flex items-center justify-center py-6 gap-2 text-slate-500 dark:text-slate-400">
                <div class="w-4 h-4 border-2 border-[#AD46FF]/30 border-t-[#AD46FF] rounded-full animate-spin"></div>
                <span class="text-sm font-medium">Loading mixed dashboard cards...</span>
              </div>
            }
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              @for (card of displayCards(); track card.key) {
                <button
                  type="button"
                  class="text-left rounded-xl border px-3 py-2.5 transition-all duration-200 group relative overflow-hidden"
                  [ngClass]="
                    isLayoutSelected(card.key)
                      ? 'border-[#AD46FF]/40 bg-violet-50/50 dark:bg-violet-500/10 shadow-sm shadow-[#AD46FF]/5'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                  "
                  (click)="toggleLayoutCard(card.key)"
                >
                  <div class="flex items-center justify-between gap-2.5 relative z-10">
                    <div class="min-w-0 flex-1">
                      <div class="font-medium text-sm truncate transition-colors"
                        [ngClass]="isLayoutSelected(card.key) ? 'text-violet-900 dark:text-violet-100' : 'text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white'"
                      >{{ card.title }}</div>
                    </div>
                    <div class="w-7 h-7 rounded-full flex items-center justify-center transition-colors shrink-0"
                      [ngClass]="isLayoutSelected(card.key) ? 'bg-white dark:bg-slate-800 shadow-sm' : 'bg-slate-100 dark:bg-slate-800 group-hover:bg-slate-200 dark:group-hover:bg-slate-700'"
                    >
                      <app-icon-v2
                        [name]="isLayoutSelected(card.key) ? 'eye' : 'eye-off'"
                        [size]="14"
                        [customClass]="isLayoutSelected(card.key) ? 'text-[#AD46FF]' : 'text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300'"
                      ></app-icon-v2>
                    </div>
                  </div>
                  @if (isLayoutSelected(card.key)) {
                    <div class="absolute inset-0 bg-gradient-to-r from-transparent to-[#AD46FF]/5 pointer-events-none"></div>
                  }
                </button>
              }
            </div>
          </div>
        </div>
      } @else {
        <div class="flex items-center justify-between mb-3">
          <div class="text-sm font-semibold text-slate-900 dark:text-white">
            Widget Catalog
          </div>
          <button mat-flat-button color="primary" class="!rounded-lg !bg-[#AD46FF] hover:!bg-[#9b3eed] !text-white !px-3 !h-8 !text-xs" (click)="openCreateWidget()">
            <span class="inline-flex items-center gap-1">
              <app-icon-v2 name="plus" [size]="14"></app-icon-v2>
              Create Widget
            </span>
          </button>
        </div>

        @if (grouped().length === 0) {
          <div class="py-12 text-center border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl bg-slate-50 dark:bg-slate-800/20">
            <div class="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-2.5">
              <app-icon-v2 name="layout-grid" [size]="20" customClass="text-slate-400 dark:text-slate-500"></app-icon-v2>
            </div>
            <div class="text-slate-900 dark:text-white font-medium text-sm mb-1">No widgets found</div>
            <div class="text-slate-500 dark:text-slate-400 text-xs">Start by creating your first widget in the catalog.</div>
          </div>
        }

        <div class="space-y-4">
          @for (g of grouped(); track $index) {
            <div>
              <div class="flex items-center gap-2 mb-2">
                <div class="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  {{ g.label }}
                </div>
                <div class="h-px flex-1 bg-slate-100 dark:bg-slate-800"></div>
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                @for (e of g.items; track e.id) {
                  <button
                    type="button"
                    class="text-left rounded-xl border px-3 py-2.5 transition-all duration-200 group relative overflow-hidden"
                    [ngClass]="
                      isSelected(e.id)
                        ? 'border-[#AD46FF]/40 bg-violet-50/50 dark:bg-violet-500/10 shadow-sm shadow-[#AD46FF]/5'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                    "
                    (click)="toggle(e.id, !isSelected(e.id))"
                  >
                    <div class="flex items-start justify-between gap-2.5 relative z-10">
                      <div class="min-w-0 flex-1">
                        <div class="font-medium text-sm truncate transition-colors"
                          [ngClass]="isSelected(e.id) ? 'text-violet-900 dark:text-violet-100' : 'text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white'"
                        >{{ e.name }}</div>
                        <div class="text-xs mt-0.5 truncate transition-colors"
                          [ngClass]="isSelected(e.id) ? 'text-violet-600/70 dark:text-violet-300/70' : 'text-slate-500 dark:text-slate-500'"
                        >{{ e.widget_key }}</div>
                      </div>
                      <div class="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          class="p-1 hover:bg-white dark:hover:bg-slate-700 rounded-lg transition-all text-slate-400 hover:text-[#AD46FF] hover:shadow-sm opacity-0 group-hover:opacity-100"
                          (click)="openEditWidget($event, e)"
                          title="Edit platform configuration"
                        >
                          <app-icon-v2 name="settings-2" [size]="14"></app-icon-v2>
                        </button>
                        <div class="w-5 h-5 rounded-full flex items-center justify-center transition-colors"
                          [ngClass]="isSelected(e.id) ? 'bg-[#AD46FF] text-white shadow-sm shadow-[#AD46FF]/20' : 'bg-transparent text-transparent'"
                        >
                          @if (isSelected(e.id)) {
                            <app-icon-v2 name="check" [size]="12"></app-icon-v2>
                          }
                        </div>
                      </div>
                    </div>
                    @if (isSelected(e.id)) {
                      <div class="absolute inset-0 bg-gradient-to-r from-transparent to-[#AD46FF]/5 pointer-events-none"></div>
                    }
                  </button>
                }
              </div>
            </div>
          }
        </div>
      }
    </mat-dialog-content>
    <mat-dialog-actions class="!bg-white dark:!bg-slate-900 !border-t !border-slate-100 dark:!border-slate-800 !px-5 !py-3 flex items-center justify-between !m-0">
      <div>
        @if (mode() === 'layout' && !isMixed() && !data.hideActions && !isLayoutActionHidden('setDefault')) {
          <button
            mat-stroked-button
            type="button"
            class="!rounded-lg !border-slate-200 dark:!border-slate-700 !text-slate-700 dark:!text-slate-300 hover:!bg-slate-50 dark:hover:!bg-slate-800 !h-8 !px-3"
            (click)="setDefaultAndApply()"
          >
            <span class="inline-flex items-center gap-1.5 text-xs">
              <app-icon-v2 name="pin" [size]="12"></app-icon-v2>
              Set default
            </span>
          </button>
        }
      </div>
      <div class="flex items-center gap-2.5">
        <button mat-button mat-dialog-close type="button" class="!rounded-lg !text-slate-600 dark:!text-slate-400 hover:!bg-slate-100 dark:hover:!bg-slate-800 !h-8 !px-3 text-sm">Cancel</button>
        <button mat-flat-button color="primary" type="button" class="!rounded-lg !bg-[#AD46FF] hover:!bg-[#9b3eed] !text-white !h-8 !px-4 text-sm" (click)="done()" [disabled]="isApplyDisabled()">
          Apply
        </button>
      </div>
    </mat-dialog-actions>
  `,
})
export class DashboardWidgetPickerDialogComponent {
  data = inject<DashboardWidgetPickerDialogData>(MAT_DIALOG_DATA);
  private dialogRef = inject(
    MatDialogRef<
      DashboardWidgetPickerDialogComponent,
      DashboardWidgetPickerDialogResult | undefined
    >,
  );
  private dialog = inject(MatDialog);
  private layoutService = inject(DashboardLayoutService);
  private readonly originalCards = this.data.cards ?? [];
  private readonly originalSelectedKeys = new Set(
    this.originalCards.filter((c) => c.selected).map((c) => c.key),
  );

  mode = computed(() => this.data.mode ?? 'widgets');
  isMixed = signal(this.data.isMixedMode ?? false);
  mixedLoading = signal(false);
  mixedDashboardId = signal<number | null>(null);
  mixedApiWidgets = signal<DashboardApiWidget[]>([]);
  mixedCurrentPageCatalog = signal<WidgetCatalogEntry[]>([]);
  mixedDataLoaded = signal(false);
  mixedCachePageId = signal<number | null>(null);
  displayCards = signal<DashboardLayoutCardItem[]>(this.originalCards);

  private selected = signal<Set<number>>(
    new Set(this.data.selectedIds ?? []),
  );
  selectedLayoutKeys = signal<Set<string>>(
    new Set((this.data.cards ?? []).filter((c) => c.selected).map((c) => c.key)),
  );
  selectedLayoutId = signal<number | null>(this.data.currentLayoutId ?? null);

  filteredEntries = computed(() => {
    let list = this.data.entries ?? [];
    if (this.data.monthlyReport1Only) {
      list = list.filter((e) => isMonthlyReport1CardId(e.widget_key));
    }
    return list;
  });
  layouts = computed(() => this.data.layouts ?? []);
  layoutChanged = computed(
    () =>
      this.selectedLayoutId() != null &&
      this.selectedLayoutId() !== (this.data.currentLayoutId ?? null),
  );

  grouped = computed(() => {
    const map = new Map<
      string,
      { label: string; items: WidgetCatalogEntry[] }
    >();
    for (const e of this.filteredEntries()) {
      const key = String(e.page_id ?? 'global');
      const label =
        e.page_id != null ? `Page ${e.page_id}` : 'Global';
      if (!map.has(key)) {
        map.set(key, { label, items: [] });
      }
      map.get(key)!.items.push(e);
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  });

  isSelected(id: number): boolean {
    return this.selected().has(id);
  }

  toggle(id: number, on: boolean): void {
    this.selected.update((s) => {
      const n = new Set(s);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });
  }

  isLayoutSelected(key: string): boolean {
    return this.selectedLayoutKeys().has(key);
  }

  toggleLayoutCard(key: string): void {
    this.selectedLayoutKeys.update((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  onLayoutChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value);
    this.selectedLayoutId.set(Number.isFinite(value) ? value : null);
  }

  emitLayoutSwitch(): void {
    const id = this.selectedLayoutId();
    if (!id) return;
    this.dialogRef.close({
      kind: 'layoutAction',
      action: 'selectLayout',
      dashboardId: id,
    });
  }

  emitLayoutAction(action: DashboardWidgetPickerAction): void {
    this.dialogRef.close({
      kind: 'layoutAction',
      action,
    });
  }

  /**
   * Checks whether a specific layout action button should be hidden.
   *
   * @param action Layout action identifier.
   * @returns True when the action button should be hidden.
   */
  isLayoutActionHidden(action: DashboardWidgetPickerAction): boolean {
    return this.data.hiddenLayoutActions?.[action] === true;
  }

  /**
   * Selects all cards and immediately applies by closing the dialog with a layout payload.
   * Requests a full default layout restore (visibility + ordering from the page reset snapshot)
   * when not in mixed-picker mode; mixed mode keeps the prior mixed batch behavior.
   *
   * @returns Void after dispatching the layout visibility result.
   */
  setDefaultAndApply(): void {
    const allKeys = this.displayCards().map((card) => card.key);
    this.selectedLayoutKeys.set(new Set(allKeys));
    const mixedContext = this.isMixed()
      ? {
          isMixed: true,
          dashboardId: this.mixedDashboardId(),
          visibleWidgets: this.buildMixedBatchPayload(),
        }
      : undefined;
    this.dialogRef.close({
      kind: 'layoutVisibility',
      selectedKeys: allKeys,
      resetLayoutToDefaults: !this.isMixed(),
      mixedContext,
    });
  }

  /**
   * Returns true when Apply should be disabled for current state.
   * Mixed mode without an existing dashboard requires at least one widget selected.
   *
   * @returns True when apply action must be disabled.
   */
  isApplyDisabled(): boolean {
    if (this.mode() === 'widgets') {
      return false;
    }
    if (!this.isMixed()) {
      return false;
    }
    if (this.mixedDashboardId() != null) {
      return false;
    }
    return this.selectedLayoutKeys().size === 0;
  }

  /**
   * Switches between page and mixed dashboard cards while keeping the dialog open.
   *
   * @returns Void signal transition after local state update / API load.
   */
  onToggleMixed(): void {
    const next = !this.isMixed();
    this.isMixed.set(next);
    if (!next) {
      this.displayCards.set(this.originalCards);
      this.selectedLayoutKeys.set(new Set(this.originalSelectedKeys));
      return;
    }
    const pageId = this.data.pageId;
    if (!pageId) {
      this.isMixed.set(false);
      return;
    }
    if (this.mixedDataLoaded() && this.mixedCachePageId() === pageId) {
      this.applyMixedCardsFromCache(pageId);
      return;
    }
    this.mixedLoading.set(true);
    forkJoin({
      mixed: this.layoutService.load(pageId, 'web', true),
      catalog: this.layoutService.getWidgetCatalog(pageId, 'web'),
    })
      .pipe(
        catchError(() => {
          this.isMixed.set(false);
          this.displayCards.set(this.originalCards);
          this.selectedLayoutKeys.set(new Set(this.originalSelectedKeys));
          this.mixedDashboardId.set(null);
          this.mixedApiWidgets.set([]);
          this.mixedCurrentPageCatalog.set([]);
          this.mixedDataLoaded.set(false);
          this.mixedCachePageId.set(null);
          return of(null);
        }),
        finalize(() => this.mixedLoading.set(false)),
      )
      .subscribe((res) => {
        if (!res) return;
        const widgets = res.mixed.widgets ?? [];
        this.mixedApiWidgets.set(widgets);
        this.mixedCurrentPageCatalog.set(res.catalog ?? []);
        this.mixedDataLoaded.set(true);
        this.mixedCachePageId.set(pageId);
        const pageIdStr = String(pageId);
        const dashboard = res.mixed.dashboard;
        const rawDashId = dashboard?.id;
        const dashId =
          typeof rawDashId === 'string'
            ? Number(rawDashId)
            : typeof rawDashId === 'number'
              ? rawDashId
              : null;
        this.mixedDashboardId.set(
          typeof dashId === 'number' && Number.isFinite(dashId) && dashId > 0 ? dashId : null,
        );
        this.applyMixedCardsFromCache(pageId);
      });
  }

  /**
   * Applies mixed response cache to the visible cards list for current page.
   *
   * @param pageId Active page id of the current dashboard screen.
   * @returns Void after updating cards and selected keys from cached mixed state.
   */
  private applyMixedCardsFromCache(pageId: number): void {
    const pageIdStr = String(pageId);
    const widgets = this.mixedApiWidgets();
    const catalog = this.mixedCurrentPageCatalog();
    const currentPageVisibleIds = new Set(
      widgets
        .filter(
          (w) =>
            w.source_page?.id != null &&
            String(w.source_page.id) === pageIdStr &&
            w.is_visible,
        )
        .map((w) => String(w.widget_id)),
    );
    const originalByWidgetId = new Map(
      this.originalCards
        .filter((card) => (card.widgetId ?? 0) > 0)
        .map((card) => [String(card.widgetId), card] as const),
    );
    const cards = catalog.map((entry) => {
      const existing = originalByWidgetId.get(String(entry.id));
      return {
        key: `widget::${entry.id}`,
        title: existing?.title ?? entry.name,
        selected: currentPageVisibleIds.has(String(entry.id)),
        sourcePageName: existing?.sourcePageName ?? null,
        widgetId: entry.id,
        sourcePageId: pageId,
        colSpan: existing?.colSpan ?? entry.default_cols ?? null,
        rowSpan: existing?.rowSpan ?? null,
        customConfig: existing?.customConfig ?? null,
      };
    });
    this.displayCards.set(cards);
    this.selectedLayoutKeys.set(
      new Set(cards.filter((card) => card.selected).map((card) => card.key)),
    );
  }

  /**
   * Builds batch payload from mixed toggle state while preserving other pages.
   *
   * @returns Visible widgets payload for mixed dashboard batch API.
   */
  private buildMixedBatchPayload(): Array<{
    widget_id: string;
    source_page_id?: string | null;
    col_span?: number | null;
    row_span?: number | null;
    custom_config?: Record<string, any> | null;
  }> {
    const pageId = this.data.pageId;
    if (pageId == null) {
      return [];
    }
    const pageIdStr = String(pageId);
    const selectedKeys = this.selectedLayoutKeys();
    const selectedCurrentPageIds = new Set(
      this.displayCards()
        .filter((card) => selectedKeys.has(card.key) && (card.widgetId ?? 0) > 0)
        .map((card) => String(card.widgetId)),
    );
    const allMixedWidgets = this.mixedApiWidgets();
    const toPayload = (w: {
      widget_id: string | number;
      source_page_id?: string | number | null;
      col_span?: number | null;
      row_span?: number | null;
      custom_config?: Record<string, any> | null;
    }) => ({
      widget_id: String(w.widget_id),
      source_page_id: w.source_page_id != null ? String(w.source_page_id) : null,
      col_span: w.col_span ?? null,
      row_span: w.row_span ?? null,
      custom_config: w.custom_config ?? null,
    });
    const isCurrentPage = (w: DashboardApiWidget): boolean => {
      if (w.source_page?.id == null) return false;
      return String(w.source_page.id) === pageIdStr;
    };

    const mixedByWidgetId = new Map(
      allMixedWidgets
        .filter((w) => isCurrentPage(w))
        .map((w) => [String(w.widget_id), w] as const),
    );
    const displayByWidgetId = new Map(
      this.displayCards()
        .filter((card) => (card.widgetId ?? 0) > 0)
        .map((card) => [String(card.widgetId), card] as const),
    );

    const currentPageSelected = [...selectedCurrentPageIds].map((widgetId) => {
      const mixed = mixedByWidgetId.get(widgetId);
      if (mixed) {
        return toPayload({
          widget_id: mixed.widget_id,
          source_page_id: mixed.source_page?.id ?? pageId,
          col_span: mixed.col_span ?? null,
          row_span: mixed.row_span ?? null,
          custom_config:
            mixed.custom_config && typeof mixed.custom_config === 'object'
              ? (mixed.custom_config as Record<string, any>)
              : null,
        });
      }
      const display = displayByWidgetId.get(widgetId);
      return toPayload({
        widget_id: widgetId,
        source_page_id: display?.sourcePageId ?? pageId,
        col_span: display?.colSpan ?? null,
        row_span: display?.rowSpan ?? null,
        custom_config: display?.customConfig ?? null,
      });
    });

    const otherPagesVisible = allMixedWidgets
      .filter((w) => !isCurrentPage(w) && w.is_visible)
      .map((w) =>
        toPayload({
          widget_id: w.widget_id,
          source_page_id: w.source_page?.id ?? null,
          col_span: w.col_span ?? null,
          row_span: w.row_span ?? null,
          custom_config:
            w.custom_config && typeof w.custom_config === 'object'
              ? (w.custom_config as Record<string, any>)
              : null,
        }),
      );

    return [...currentPageSelected, ...otherPagesVisible];
  }

  done(): void {
    if (this.mode() === 'layout') {
      if (this.isApplyDisabled()) {
        return;
      }
      const mixedContext = this.isMixed()
        ? {
            isMixed: true,
            dashboardId: this.mixedDashboardId(),
            visibleWidgets: this.buildMixedBatchPayload(),
          }
        : undefined;
      this.dialogRef.close({
        kind: 'layoutVisibility',
        selectedKeys: [...this.selectedLayoutKeys()],
        mixedContext,
      });
      return;
    }
    this.dialogRef.close({
      kind: 'widgets',
      selectedIds: [...this.selected()],
    });
  }

  openCreateWidget() {
    this.dialog.open(DashboardCatalogWidgetFormDialogComponent, {
      data: { pageId: this.data.pageId },
      maxWidth: '600px',
      width: '90%'
    }).afterClosed().subscribe(refresh => {
      if (refresh) {
        // Since we can't easily refresh the data in the dialog without parent help,
        // we'll signal the parent to refresh by closing with a special result.
        this.dialogRef.close({
          kind: 'layoutAction',
          action: 'widgets' // Triggering same action usually forces a refresh in parent
        });
      }
    });
  }

  openEditWidget(event: MouseEvent, e: WidgetCatalogEntry) {
    event.stopPropagation();
    this.dialog.open(DashboardCatalogWidgetFormDialogComponent, {
      data: { 
        pageId: this.data.pageId,
        widget: e,
        platform: WidgetPlatform.WEB // Default to web for editing
      },
      maxWidth: '600px',
      width: '90%'
    }).afterClosed().subscribe(refresh => {
      if (refresh) {
        this.dialogRef.close({
          kind: 'layoutAction',
          action: 'widgets'
        });
      }
    });
  }
}
